/**
 * JWT Utility Functions
 * Handles JWT token parsing and extraction
 */

/**
 * Decode a JWT token without verification (for client-side use only)
 * @param {string} token - JWT token string
 * @returns {Object|null} - Decoded payload or null if invalid
 */
export function decodeJWT(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64 decode and parse JSON
    const decodedPayload = JSON.parse(atob(payload));
    
    return decodedPayload;
  } catch (error) {
    console.error('[JWT Utils] Failed to decode token:', error);
    return null;
  }
}

/**
 * Extract federation mode from JWT token
 * @param {string} token - JWT token string
 * @returns {string|null} - Federation mode ('hospital', 'asc') or null
 */
export function extractFederationMode(token) {
  const payload = decodeJWT(token);
  if (!payload) return null;
  // Federation may be encoded as an object { mode: 'hospital' | 'asc', ... } or a string
  const fed = payload.federation;
  if (typeof fed === 'object' && fed !== null) {
    return fed.mode || null;
  }
  return fed || payload.mode || null;
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} - True if expired, false otherwise
 */
export function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  return Date.now() > expirationTime;
}

/**
 * Get user info from JWT token
 * @param {string} token - JWT token string
 * @returns {Object|null} - User info object or null
 */
export function getUserFromToken(token) {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  return {
    id: payload.userId,
    username: payload.username,
    email: payload.email,
    role: payload.role,
    federation: payload.federation,
    mode: payload.federation || payload.mode,
    isDemo: payload.isDemo || false,
  };
}